import { openDB }  from '@utility/indexedDB_utils';

// TODO: unify the db name/version and utility functions across pages

export const getIndexedDBData = async (storeName) => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction(storeName, "readonly");
        transaction.onerror = (error) => reject(error);
  
        const store = transaction.objectStore(storeName);
        const request = store.getAll();
  
        request.onsuccess = () => resolve(request.result || []);
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error(`Error accessing ${storeName}:`, error);
      return [];
    }
};

export const saveCurrentWorkoutSession = async (sessionData) => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_workout_session", "readwrite");
        const store = transaction.objectStore("current_workout_session");

        store.clear().onsuccess = () => {
          // remove id from sessionData to let it ++
          const { id, ...dataWithoutId } = sessionData;
          const request = store.add(dataWithoutId);
          request.onsuccess = () => resolve(request.result);
          request.onerror = (error) => reject(error);
        };
      });
    } catch (error) {
      console.error("Error saving workout session:", error);
      throw error;
    }
};

export const getMaxSessionId = async () => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("workout_sessions", "readonly");
        const store = transaction.objectStore("workout_sessions");
        const request = store.getAllKeys();

        request.onsuccess = () => {
          //console.log("Keys retrieved:", request.result);
          const keys = request.result;
          if (keys.length === 0) {
            resolve(0);
          } else {
            resolve(Math.max(...keys));
          }
        };

        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error getting max session ID:", error);
      return 0;
    }
};

export const getMaxTemplateId = async () => {
  try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("workout_templates", "readonly");
        const store = transaction.objectStore("workout_templates");
        const request = store.getAllKeys();

        request.onsuccess = () => {
          const keys = request.result;
          if (keys.length === 0) {
            resolve(0);
          } else {
            resolve(Math.max(...keys));
          }
        };

        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error getting max template ID:", error);
      return 0;
    }
}

export const getCurrentWorkoutSession = async () => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_workout_session", "readonly");
        const store = transaction.objectStore("current_workout_session");
        const request = store.getAll();

        request.onsuccess = () => resolve(request.result[0] || null);
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error getting workout session:", error);
      return null;
    }
};

export const clearCurrentWorkoutSession = async () => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_workout_session", "readwrite");
        const store = transaction.objectStore("current_workout_session");
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error clearing workout session:", error);
      throw error;
    }
};

export const saveCurrentWorkoutLog = async (logData) => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_workout_log", "readwrite");
        const store = transaction.objectStore("current_workout_log");
        const request = store.add(logData);

        request.onsuccess = () => resolve(request.result);
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error saving workout log:", error);
      throw error;
    }
};

export const getCurrentWorkoutLogs = async (sessionId) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("current_workout_log", "readonly");
      const store = transaction.objectStore("current_workout_log");

      // if index doesn't exist
      if (!store.indexNames.contains("session_id")) {
        const getAllRequest = store.getAll();
        getAllRequest.onsuccess = () => {
          resolve(getAllRequest.result.filter(log => log.session_id === sessionId));
        };
        getAllRequest.onerror = (error) => reject(error);
      } else {
        const index = store.index("session_id");
        const indexRequest = index.getAll(sessionId);
        indexRequest.onsuccess = () => resolve(indexRequest.result || []);
        indexRequest.onerror = (error) => reject(error);
      }
    });
};

export const updateCurrentWorkoutLog = async (logId, updates) => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_workout_log", "readwrite");
        const store = transaction.objectStore("current_workout_log");

        const getRequest = store.get(logId);

        getRequest.onsuccess = () => {
          const existingLog = getRequest.result;
          if (!existingLog) {
            reject(new Error("Log not found"));
            return;
          }

          const updatedLog = { ...existingLog, ...updates };
          const putRequest = store.put(updatedLog);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = (error) => reject(error);
        };

        getRequest.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error updating workout log:", error);
      throw error;
    }
};

export const saveCurrentSet = async (setData) => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_sets", "readwrite");
        const store = transaction.objectStore("current_sets");
        const request = store.add(setData);

        request.onsuccess = () => resolve(request.result);
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error saving set:", error);
      throw error;
    }
};

export const getCurrentSets = async (workoutLogId) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction("current_sets", "readonly");
      const store = transaction.objectStore("current_sets");

      // if index doesn't exist
      if (!store.indexNames.contains("workout_log_id")) {
        const getAllRequest = store.getAll();
        getAllRequest.onsuccess = () => {
          resolve(getAllRequest.result.filter(set => set.workout_log_id === workoutLogId));
        };
        getAllRequest.onerror = (error) => reject(error);
      } else {
        const index = store.index("workout_log_id");
        const indexRequest = index.getAll(workoutLogId);
        indexRequest.onsuccess = () => resolve(indexRequest.result || []);
        indexRequest.onerror = (error) => reject(error);
      }
    });
};

export const updateCurrentSet = async (setId, updates) => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_sets", "readwrite");
        const store = transaction.objectStore("current_sets");

        const getRequest = store.get(setId);

        getRequest.onsuccess = () => {
          const existingSet = getRequest.result;
          if (!existingSet) {
            reject(new Error("Set not found"));
            return;
          }

          const updatedSet = { ...existingSet, ...updates };
          const putRequest = store.put(updatedSet);
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = (error) => reject(error);
        };

        getRequest.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error updating set:", error);
      throw error;
    }
};

export const deleteCurrentSet = (setId) => {
  return new Promise((resolve, reject) => {
    openDB().then(db => {
      const transaction = db.transaction(['current_sets'], 'readwrite');
      const store = transaction.objectStore('current_sets');
      const request = store.delete(setId);

      request.onsuccess = () => resolve();
      request.onerror = (error) => {
        console.error("Delete set error:", error);
        reject(error);
      };
    }).catch(reject);
  });
};

export const clearCurrentSets = async () => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_sets", "readwrite");
        const store = transaction.objectStore("current_sets");
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error clearing sets:", error);
      throw error;
    }
};

export const clearCurrentWorkoutLogs = async () => {
    try {
      const db = await openDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction("current_workout_log", "readwrite");
        const store = transaction.objectStore("current_workout_log");
        const request = store.clear();

        request.onsuccess = () => resolve();
        request.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error clearing workout logs:", error);
      throw error;
    }
};

export const clearCurrentWorkoutData = async () => {
    try {
      await Promise.all([
        clearCurrentWorkoutSession(),
        clearCurrentWorkoutLogs(),
        clearCurrentSets()
      ]);
    } catch (error) {
      console.error("Error clearing workout data:", error);
      throw error;
    }
};

export const loadActiveWorkoutFromDB = async () => {
    try {
      const session = await getCurrentWorkoutSession();
      if (!session) return null;

      const logs = (await getCurrentWorkoutLogs(session.id))
        .sort((a, b) => a.exercise_index - b.exercise_index);

      // wait for exercises to be loaded
      const exercisesData = await getIndexedDBData("exercises");

      const workoutExercises = await Promise.all(logs.map(async (log) => {
        try {
          const exercise = exercisesData.find(e => e.id === log.exercise_id);
          const sets = await getCurrentSets(log.id);

          return {
            id: log.exercise_id,
            name: exercise?.name || `Exercise ${log.exercise_id}`,
            sets: sets.map((set, i) => ({
              id: set.id,
              setNumber: i + 1,
              targetWeight: set.weight,
              targetReps: set.reps,
              completedWeight: set.completed ? set.weight : null,
              completedReps: set.completed ? set.reps : null,
              completed: set.completed || false,
              partial: set.partial ?? false,
              drop_set_group: set.drop_set_group || 0
            }))
          };
        } catch (error) {
          console.error(`Error loading exercise ${log.exercise_id}:`, error);
          return null;
        }
      }));

      return {
        id: session.id,
        name: session.session_name,
        exercises: workoutExercises.filter(Boolean),
        startedAt: session.session_date
      };
    } catch (error) {
      console.error("Error loading workout:", error);
      return null;
    }
};

export const getWorkoutTemplatesExercises = (workoutTemplatesExercises, id) => {
    return workoutTemplatesExercises.filter(exercise => exercise.template_id === id);
};

export const formatExerciseList = (exercises, list) => {
  return list.map(listItem => {
    const exercise = exercises.find(ex => ex.id === listItem.exercise_id);
    if (!exercise) return null;

    const setsInfo = listItem.set_details
      .map(set => {
        if (set.weight == null) {
          return `${set.reps} reps`;
        }
        return `${set.weight}kg × ${set.reps}`;
      })
      .join('\n');

    return `${exercise.name}:\n${setsInfo}`;
  }).filter(Boolean);
};

export const saveLastExercisePerformance = async (exerciseId, sets) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["last_exercise_performance"], "readwrite");
    const store = transaction.objectStore("last_exercise_performance");
    
    const completedSets = sets
      .filter(set => set.completed)
      .map(set => ({
        weight: set.completedWeight ?? set.targetWeight,
        reps: set.completedReps ?? set.targetReps,
        partial: set.partial ?? false,
        drop_set_group: set.drop_set_group ?? 0
      }));

    if (completedSets.length === 0) {
      resolve();
      return;
    }

    const dataToSave = {
      exercise_id: exerciseId,
      last_session_date: new Date().toISOString(),
      sets: completedSets
    };

    // Explicitly delete any id field that might exist
    delete dataToSave.id;

    const request = store.put(dataToSave);

    request.onsuccess = () => resolve();
    request.onerror = (e) => {
      console.error("Error saving exercise performance:", e);
      reject(e);
    };
  });
};

export const getLastExercisePerformance = async (exerciseId) => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(["last_exercise_performance"], "readonly");
      const store = transaction.objectStore("last_exercise_performance");
      
      const request = store.get(exerciseId);
      
      request.onsuccess = (e) => {
        const result = e.target.result;
        if (result) {
          console.groupCollapsed(`[DEBUG] Loaded last performance for ${exerciseId}`);
            console.log('Last session date:', result.last_session_date);
            console.log('Sets:', result.sets.map(set => ({
              weight: set.weight,
              reps: set.reps,
              partial: set.partial,
              drop_set_group: set.drop_set_group
            })));
          console.groupEnd();
          
          resolve({
            last_session_date: result.last_session_date,
            sets: result.sets.map(set => ({
              weight: set.weight,
              reps: set.reps,
              partial: set.partial || false,
              drop_set_group: set.drop_set_group || 0
            }))
          });
        } else {
          console.log(`[DEBUG] No previous performance found for ${exerciseId}`);
          resolve(null);
        }
      };
      
      request.onerror = (e) => {
        console.error("[ERROR] Failed to get performance:", exerciseId, e.target.error);
        reject(new Error("Failed to retrieve last performance data"));
      };
      
      transaction.onerror = (e) => {
        console.error("[ERROR] Transaction failed:", e.target.error);
        reject(new Error("Database transaction failed"));
      };
    });
  } catch (error) {
    console.error("[ERROR] Database open failed:", error);
    throw new Error("Could not access exercise performance database");
  }
};

export const cleanupExercisePerformanceData = async () => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["last_exercise_performance"], "readwrite");
    const store = transaction.objectStore("last_exercise_performance");
    
    // Get all records to find the most recent for each exercise
    const getAllRequest = store.getAll();
    
    getAllRequest.onsuccess = () => {
      const allRecords = getAllRequest.result;
      const mostRecentRecords = new Map();
      
      // Find the most recent record for each exercise
      allRecords.forEach(record => {
        const existing = mostRecentRecords.get(record.exercise_id);
        if (!existing || new Date(record.last_session_date) > new Date(existing.last_session_date)) {
          mostRecentRecords.set(record.exercise_id, record);
        }
      });
      
      // Clear ALL data first
      store.clear().onsuccess = () => {
        // Then add back only the most recent records
        const putOperations = Array.from(mostRecentRecords.values()).map(record => {
          return new Promise((innerResolve) => {
            const putRequest = store.put(record);
            putRequest.onsuccess = innerResolve;
            putRequest.onerror = innerResolve;
          });
        });
        
        Promise.all(putOperations).then(resolve).catch(resolve);
      };
    };
    
    getAllRequest.onerror = reject;
  });
};

export const saveExerciseMuscles = async (exerciseId, primaryMuscles, secondaryMuscles) => {
  const db = await openDB();
  const transaction = db.transaction(
    ["exercise_primary_muscles", "exercise_secondary_muscles"],
    "readwrite"
  );

  const primaryStore = transaction.objectStore("exercise_primary_muscles");
  const secondaryStore = transaction.objectStore("exercise_secondary_muscles");

  // Delete existing primary muscle relationships
  await new Promise(resolve => {
    const primaryDeleteRequest = primaryStore.index("exercise_id")
      .openCursor(IDBKeyRange.only(exerciseId));
    
    primaryDeleteRequest.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
  });

  // Delete existing secondary muscle relationships
  await new Promise(resolve => {
    const secondaryDeleteRequest = secondaryStore.index("exercise_id")
      .openCursor(IDBKeyRange.only(exerciseId));
    
    secondaryDeleteRequest.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      } else {
        resolve();
      }
    };
  });

  // Add new primary muscles with percent
  await Promise.all(primaryMuscles.map(({ muscle_id, percent }) => {
    return new Promise(resolve => {
      const request = primaryStore.add({
        exercise_id: exerciseId,
        muscle_id,
        percent
      });
      request.onsuccess = resolve;
    });
  }));

  // Add new secondary muscles with percent
  await Promise.all(secondaryMuscles.map(({ muscle_id, percent }) => {
    return new Promise(resolve => {
      const request = secondaryStore.add({
        exercise_id: exerciseId,
        muscle_id,
        percent
      });
      request.onsuccess = resolve;
    });
  }));

  return new Promise((resolve, reject) => {
    transaction.oncomplete = resolve;
    transaction.onerror = reject;
  });
};


export const deleteWorkoutTemplate = async (templateId) => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      ["workout_templates", "workout_template_exercises"], 
      "readwrite"
    );
    
    // Delete template
    const templateStore = transaction.objectStore("workout_templates");
    templateStore.delete(templateId);
    
    // Delete all exercises
    const exercisesStore = transaction.objectStore("workout_template_exercises");
    const index = exercisesStore.index("template_id");
    const request = index.openCursor(IDBKeyRange.only(templateId));
    
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = (error) => reject(error);
  });
};

export const addMuscle = async (muscleName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("muscles", "readwrite");
    const store = transaction.objectStore("muscles");
    const request = store.add({ name: muscleName });

    request.onsuccess = () => resolve(request.result);
    request.onerror = (error) => reject(error);
  });
};

export const updateMuscle = async (muscleId, newName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("muscles", "readwrite");
    const store = transaction.objectStore("muscles");
    
    const getRequest = store.get(muscleId);
    getRequest.onsuccess = () => {
      const muscle = getRequest.result;
      if (!muscle) {
        reject(new Error("Muscle not found"));
        return;
      }
      
      muscle.name = newName;
      const putRequest = store.put(muscle);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = (error) => reject(error);
    };
    getRequest.onerror = (error) => reject(error);
  });
};

export const deleteMuscle = async (muscleId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["muscles", "exercise_primary_muscles", "exercise_secondary_muscles", "muscle_to_group"], "readwrite");
    
    // Delete from muscles
    const musclesStore = transaction.objectStore("muscles");
    musclesStore.delete(muscleId);
    
    // Delete from primary muscles
    const primaryStore = transaction.objectStore("exercise_primary_muscles");
    const primaryIndex = primaryStore.index("muscle_id");
    const primaryCursor = primaryIndex.openCursor(IDBKeyRange.only(muscleId));
    primaryCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    // Delete from secondary muscles
    const secondaryStore = transaction.objectStore("exercise_secondary_muscles");
    const secondaryIndex = secondaryStore.index("muscle_id");
    const secondaryCursor = secondaryIndex.openCursor(IDBKeyRange.only(muscleId));
    secondaryCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    // Delete from muscle_to_group
    const groupStore = transaction.objectStore("muscle_to_group");
    const groupIndex = groupStore.index("muscle_id");
    const groupCursor = groupIndex.openCursor(IDBKeyRange.only(muscleId));
    groupCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = (error) => reject(error);
  });
};

// Muscle Group CRUD operations
export const addMuscleGroup = async (groupName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("muscle_groups", "readwrite");
    const store = transaction.objectStore("muscle_groups");
    const request = store.add({ name: groupName });

    request.onsuccess = () => resolve(request.result);
    request.onerror = (error) => reject(error);
  });
};

export const updateMuscleGroup = async (groupId, newName) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("muscle_groups", "readwrite");
    const store = transaction.objectStore("muscle_groups");
    
    const getRequest = store.get(groupId);
    getRequest.onsuccess = () => {
      const group = getRequest.result;
      if (!group) {
        reject(new Error("Group not found"));
        return;
      }
      
      group.name = newName;
      const putRequest = store.put(group);
      putRequest.onsuccess = () => resolve();
      putRequest.onerror = (error) => reject(error);
    };
    getRequest.onerror = (error) => reject(error);
  });
};

export const deleteMuscleGroup = async (groupId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(["muscle_groups", "muscle_to_group"], "readwrite");
    
    // Delete from muscle_groups
    const groupsStore = transaction.objectStore("muscle_groups");
    groupsStore.delete(groupId);
    
    // Delete from muscle_to_group
    const relationStore = transaction.objectStore("muscle_to_group");
    const relationIndex = relationStore.index("group_id");
    const relationCursor = relationIndex.openCursor(IDBKeyRange.only(groupId));
    relationCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = (error) => reject(error);
  });
};

// Muscle-Group Relationship operations
export const addMuscleToGroup = async (muscleId, groupId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("muscle_to_group", "readwrite");
    const store = transaction.objectStore("muscle_to_group");
    
    // First check if relationship already exists
    const index = store.index("muscle_id");
    const request = index.openCursor(IDBKeyRange.only(muscleId));
    
    let exists = false;
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.group_id === groupId) {
          exists = true;
        }
        cursor.continue();
      } else {
        if (exists) {
          resolve(); // Relationship already exists
        } else {
          const addRequest = store.add({ muscle_id: muscleId, group_id: groupId });
          addRequest.onsuccess = () => resolve();
          addRequest.onerror = (error) => reject(error);
        }
      }
    };
    request.onerror = (error) => reject(error);
  });
};

export const removeMuscleFromGroup = async (muscleId, groupId) => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("muscle_to_group", "readwrite");
    const store = transaction.objectStore("muscle_to_group");
    const index = store.index("muscle_id");
    
    const request = index.openCursor(IDBKeyRange.only(muscleId));
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.group_id === groupId) {
          cursor.delete();
        }
        cursor.continue();
      } else {
        resolve();
      }
    };
    request.onerror = (error) => reject(error);
  });
};

export const getMusclesWithGroups = async () => {
  const db = await openDB();
  return new Promise(async (resolve, reject) => {
    try {
      // Get all muscles
      const muscles = await new Promise((res, rej) => {
        const tx = db.transaction("muscles", "readonly");
        const store = tx.objectStore("muscles");
        const req = store.getAll();
        req.onsuccess = () => res(req.result);
        req.onerror = (e) => rej(e);
      });
      
      // Get all groups
      const groups = await new Promise((res, rej) => {
        const tx = db.transaction("muscle_groups", "readonly");
        const store = tx.objectStore("muscle_groups");
        const req = store.getAll();
        req.onsuccess = () => res(req.result);
        req.onerror = (e) => rej(e);
      });
      
      // Get all relationships
      const relationships = await new Promise((res, rej) => {
        const tx = db.transaction("muscle_to_group", "readonly");
        const store = tx.objectStore("muscle_to_group");
        const req = store.getAll();
        req.onsuccess = () => res(req.result);
        req.onerror = (e) => rej(e);
      });
      
      // Combine the data
      const result = muscles.map(muscle => {
        const muscleGroups = relationships
          .filter(r => r.muscle_id === muscle.id)
          .map(r => {
            const group = groups.find(g => g.id === r.group_id);
            return group ? { id: group.id, name: group.name } : null;
          })
          .filter(Boolean);
        
        return {
          ...muscle,
          groups: muscleGroups
        };
      });
      
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

export const getMuscleGroupsWithMuscles = async () => {
  const db = await openDB();
  return new Promise(async (resolve, reject) => {
    try {
      // Get all groups
      const groups = await new Promise((res, rej) => {
        const tx = db.transaction("muscle_groups", "readonly");
        const store = tx.objectStore("muscle_groups");
        const req = store.getAll();
        req.onsuccess = () => res(req.result);
        req.onerror = (e) => rej(e);
      });
      
      // Get all muscles
      const muscles = await new Promise((res, rej) => {
        const tx = db.transaction("muscles", "readonly");
        const store = tx.objectStore("muscles");
        const req = store.getAll();
        req.onsuccess = () => res(req.result);
        req.onerror = (e) => rej(e);
      });
      
      // Get all relationships
      const relationships = await new Promise((res, rej) => {
        const tx = db.transaction("muscle_to_group", "readonly");
        const store = tx.objectStore("muscle_to_group");
        const req = store.getAll();
        req.onsuccess = () => res(req.result);
        req.onerror = (e) => rej(e);
      });
      
      // Combine the data - group by muscle groups
      const result = groups.map(group => {
        const groupMuscles = relationships
          .filter(r => r.group_id === group.id)
          .map(r => {
            const muscle = muscles.find(m => m.id === r.muscle_id);
            return muscle ? { id: muscle.id, name: muscle.name } : null;
          })
          .filter(Boolean);
        
        return {
          ...group,
          muscles: groupMuscles
        };
      });
      
      // Add ungrouped muscles
      const ungroupedMuscles = muscles.filter(muscle => 
        !relationships.some(r => r.muscle_id === muscle.id)
      );
      
      if (ungroupedMuscles.length > 0) {
        result.push({
          id: null,
          name: "Ungrouped",
          muscles: ungroupedMuscles
        });
      }
      
      resolve(result);
    } catch (error) {
      reject(error);
    }
  });
};

export const addNewCustomExercise = async (
  newExerciseData,
  exerciseModalContext,
  activeWorkout,
  exercises,
  setExercises,
  setActiveWorkout,
  setEditedTemplate,
  setShowExerciseModal,
  setShowNewExerciseForm,
  setNewExerciseData,
  setError
) => {
  try {
    if (!newExerciseData.name.trim()) {
      setError("Exercise name is required");
      return;
    }

    const db = await openDB();

    // add exercise to the exercises table
    const exerciseId = await new Promise((resolve, reject) => {
      const transaction = db.transaction(["exercises"], "readwrite");
      const exerciseStore = transaction.objectStore("exercises");
      
      const newExercise = {
        name: newExerciseData.name.trim(),
        is_isolated: newExerciseData.is_isolated,
        category: newExerciseData.category
      };

      const request = exerciseStore.add(newExercise);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(new Error("Failed to create exercise"));
    });

    await saveExerciseMuscles(
      exerciseId,
      newExerciseData.primaryMuscles,
      newExerciseData.secondaryMuscles
    );

    // Update local exercises state to include the new exercise
    const updatedExercises = [
      ...exercises, 
      {
        id: exerciseId,
        name: newExerciseData.name.trim(),
        is_isolated: newExerciseData.is_isolated,
        category: newExerciseData.category
      }
    ];
    setExercises(updatedExercises);

    // Now handle the context-specific addition
    if (exerciseModalContext === 'workout') {
      // Add to active workout
      const logEntry = {
        session_id: activeWorkout.id,
        exercise_id: exerciseId,
        sets_count: 1,
        total_weight_lifted: 0,
        notes: "",
        exercise_index: activeWorkout.exercises.length
      };

      const logId = await saveCurrentWorkoutLog(logEntry);
      await saveCurrentSet({
        workout_log_id: logId,
        weight: 0,
        reps: 0,
        drop_set_group: 0,
        partial: false,
        completed: false
      });

      setActiveWorkout(prev => ({
        ...prev,
        exercises: [
          ...prev.exercises,
          {
            id: exerciseId,
            name: newExerciseData.name.trim(),
            sets: [{
              setNumber: 1,
              targetWeight: 0,
              targetReps: 0,
              completedWeight: null,
              completedReps: null,
              completed: false,
              partial: false,
              drop_set_group: 0
            }]
          }
        ]
      }));
    } else {
      // Add to template being edited
      setEditedTemplate(prev => ({
        ...prev,
        exercises: [
          ...prev.exercises,
          {
            id: exerciseId,
            name: newExerciseData.name.trim(),
            sets: [{
              setNumber: 1,
              weight: 0,
              reps: 0,
              partial: false,
              drop_set_group: 0
            }]
          }
        ]
      }));
    }

    // Reset form and close modal
    setShowExerciseModal(false);
    setShowNewExerciseForm(false);
    setNewExerciseData({
      name: '',
      is_isolated: false,
      category: 'Freeweight'
    });

  } catch (error) {
    console.error("Error adding new exercise:", error);
    setError(`Failed to create exercise: ${error.message}`);
  }
};

export const deleteExerciseFromDB = async (exerciseId) => {
  const db = await openDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(
      [
        "exercises",
        "current_workout_log",
        "current_sets",
        "workout_log",
        "sets",
        "workout_template_exercises",
        "last_exercise_performance",
        "exercise_primary_muscles",
        "exercise_secondary_muscles"
      ],
      "readwrite"
    );

    // Delete from exercises table
    const exercisesStore = transaction.objectStore("exercises");
    exercisesStore.delete(exerciseId);

    // Delete from last_exercise_performance
    const performanceStore = transaction.objectStore("last_exercise_performance");
    performanceStore.delete(exerciseId);

    // Delete from exercise_primary_muscles
    const primaryMusclesStore = transaction.objectStore("exercise_primary_muscles");
    const primaryIndex = primaryMusclesStore.index("exercise_id");
    const primaryCursor = primaryIndex.openCursor(IDBKeyRange.only(exerciseId));
    primaryCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Delete from exercise_secondary_muscles
    const secondaryMusclesStore = transaction.objectStore("exercise_secondary_muscles");
    const secondaryIndex = secondaryMusclesStore.index("exercise_id");
    const secondaryCursor = secondaryIndex.openCursor(IDBKeyRange.only(exerciseId));
    secondaryCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Delete from workout_template_exercises
    const templateExercisesStore = transaction.objectStore("workout_template_exercises");
    const templateIndex = templateExercisesStore.index("exercise_id");
    const templateCursor = templateIndex.openCursor(IDBKeyRange.only(exerciseId));
    templateCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };

    // Delete from current_workout_log and current_sets
    const currentLogStore = transaction.objectStore("current_workout_log");
    const currentLogIndex = currentLogStore.index("exercise_id");
    const currentLogCursor = currentLogIndex.openCursor(IDBKeyRange.only(exerciseId));
    
    currentLogCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        // Delete associated sets first
        const currentSetsStore = transaction.objectStore("current_sets");
        const setsIndex = currentSetsStore.index("workout_log_id");
        const setsCursor = setsIndex.openCursor(IDBKeyRange.only(cursor.value.id));
        
        setsCursor.onsuccess = (setsEvent) => {
          const setsCursor = setsEvent.target.result;
          if (setsCursor) {
            setsCursor.delete();
            setsCursor.continue();
          } else {
            // After deleting sets, delete the log entry
            cursor.delete();
            cursor.continue();
          }
        };
      }
    };

    // Delete from workout_log and sets (permanent records)
    const logStore = transaction.objectStore("workout_log");
    const logIndex = logStore.index("exercise_id");
    const logCursor = logIndex.openCursor(IDBKeyRange.only(exerciseId));
    
    logCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        // Delete associated sets first
        const setsStore = transaction.objectStore("sets");
        const setsIndex = setsStore.index("workout_log_id");
        const setsCursor = setsIndex.openCursor(IDBKeyRange.only(cursor.value.id));
        
        setsCursor.onsuccess = (setsEvent) => {
          const setsCursor = setsEvent.target.result;
          if (setsCursor) {
            setsCursor.delete();
            setsCursor.continue();
          } else {
            // After deleting sets, delete the log entry
            cursor.delete();
            cursor.continue();
          }
        };
      }
    };

    transaction.oncomplete = () => resolve();
    transaction.onerror = (error) => {
      console.error("Error deleting exercise:", error);
      reject(error);
    };
  });
};

export const getExerciseMuscles = async (exerciseId) => {
  const db = await openDB();
  
  const [primary, secondary] = await Promise.all([
    new Promise((resolve) => {
      const transaction = db.transaction('exercise_primary_muscles', 'readonly');
      const store = transaction.objectStore('exercise_primary_muscles');
      const index = store.index('exercise_id');
      const request = index.getAll(exerciseId);
      
      request.onsuccess = () => resolve(request.result || []);
    }),
    
    new Promise((resolve) => {
      const transaction = db.transaction('exercise_secondary_muscles', 'readonly');
      const store = transaction.objectStore('exercise_secondary_muscles');
      const index = store.index('exercise_id');
      const request = index.getAll(exerciseId);
      
      request.onsuccess = () => resolve(request.result || []);
    })
  ]);

  return {
    primary: primary.map(m => ({
      muscle_id: m.muscle_id,
      percent: m.percent
    })),
    secondary: secondary.map(m => ({
      muscle_id: m.muscle_id,
      percent: m.percent
    }))
  };
};

export const resetExercisePerformanceStore = async () => {
  const db = await openDB();
  return new Promise((resolve) => {
    const transaction = db.transaction(
      ["last_exercise_performance"], 
      "readwrite"
    );
    
    const store = transaction.objectStore("last_exercise_performance");
    const clearRequest = store.clear();
    
    clearRequest.onsuccess = () => {
      console.log("Store cleared successfully");
      resolve();
    };
    
    clearRequest.onerror = () => {
      console.error("Failed to clear store");
      resolve();
    };
  });
};